import SmsSubscription from '../../../types/sms/SmsSubscription';
import { fixPhoneNumberFormat } from '../../../util/phoneNumberValidation';
import { saveSubscription, unsubscribe } from '../../../apis/omniApi';

export function makeAlertData(
  e: Error | null,
  alertMsg: string,
  updateAlert: (arg: boolean, message: string) => void
): void {
  updateAlert(!!e, alertMsg);
}

function isSaveButtonEnabledForOrderConfirmation(
  phoneNumberError: boolean,
  currentOptInValue: boolean
): boolean {
  return !phoneNumberError && currentOptInValue;
}

function isSaveButtonEnabledForManageTexts(
  subscription: SmsSubscription | null,
  newPhoneNumber: string,
  phoneNumberError: boolean,
  currentOptInValue: boolean
): boolean {
  let retValue = currentOptInValue;

  // don't enable for an invalid phone number
  if (phoneNumberError) {
    retValue = false;
  }

  try {
    const fixedNewPhoneNumber = fixPhoneNumberFormat(newPhoneNumber, true);

    if (subscription?.phoneNumber === fixedNewPhoneNumber) {
      if (currentOptInValue) {
        retValue = false;
      }
    }
  } catch (e) {
    retValue = false;
  }

  // if the person wants to unsubscribe, we don't care what else is going on
  if (subscription && !currentOptInValue) {
    retValue = true;
  }

  return retValue;
}

export function isSaveButtonEnabledForSmsModal(
  subscription: SmsSubscription | null,
  newPhoneNumber: string,
  phoneNumberError: boolean,
  currentOptInValue: boolean,
  isConfirmationPage: boolean
): boolean {
  if (isConfirmationPage) {
    return isSaveButtonEnabledForOrderConfirmation(
      phoneNumberError,
      currentOptInValue
    );
  }

  return isSaveButtonEnabledForManageTexts(
    subscription,
    newPhoneNumber,
    phoneNumberError,
    currentOptInValue
  );
}

export const ENROLLMENT_SUCCESS_MESSAGE_KEY = 'enrollmentSuccess';
export const PHONE_NUMBER_CHANGE_SUCCESS_MESSAGE_KEY =
  'phoneNumberChangeSuccess';
export const ERROR_ON_SUBSCRIPTION_CHANGE_MESSAGE_KEY =
  'errorOnSubscriptionChange';
export const UNENROLLMENT_SUCCESS_MESSAGE_KEY = 'unenrollmentSuccess';
export const UNSUBSCRIBE_SUCCESS_MESSAGE_KEY = 'unsubscribeSuccess';

export const updateSubscription = async (
  orderNumber: string | null,
  phoneNumber: string,
  shopperId: string | null,
  tenantEnvironment: string | null,
  isNewSubscription: boolean
) => {
  let alertMessage: string | null = '';
  let error: Error | null = null;
  const subscriptionRequest: SmsSubscription = {
    orderNumber,
    phoneNumber,
    shopperId,
  };
  try {
    subscriptionRequest.phoneNumber = fixPhoneNumberFormat(
      subscriptionRequest.phoneNumber,
      true
    );

    await saveSubscription(
      tenantEnvironment,
      subscriptionRequest,
      isNewSubscription
    );
    if (isNewSubscription) {
      alertMessage = ENROLLMENT_SUCCESS_MESSAGE_KEY;
    } else {
      alertMessage = PHONE_NUMBER_CHANGE_SUCCESS_MESSAGE_KEY;
    }
  } catch (e) {
    error = e as Error;
    alertMessage = ERROR_ON_SUBSCRIPTION_CHANGE_MESSAGE_KEY;
  }
  return { alertMessage, error };
};

export const deleteSubscription = async (
  orderNumber: string | null,
  tenantEnvironment: string | null
) => {
  let alertMessage: string | null = '';
  let error: Error | null = null;
  try {
    await unsubscribe(tenantEnvironment, orderNumber).then(
      phoneNumberUnsubscribed => {
        // phoneNumberUnsubscribed is returned when we have sent a
        // message to TSS to remove the subscription from Attentive
        if (phoneNumberUnsubscribed) {
          alertMessage = UNENROLLMENT_SUCCESS_MESSAGE_KEY;
        } else {
          alertMessage = UNSUBSCRIBE_SUCCESS_MESSAGE_KEY;
        }
      }
    );
  } catch (e) {
    error = e as Error;
    alertMessage = ERROR_ON_SUBSCRIPTION_CHANGE_MESSAGE_KEY;
  }
  return { alertMessage, error };
};
